/**
 * UnderwayPhase.js
 *
 * Underway phase interface - the primary operational screen
 *
 * V2 REFACTOR: Now a thin wrapper around VoyageDocument.
 * Operations appear INLINE in the unified voyage log, not as separate panels.
 *
 * Features:
 * - VoyageDocument renders the entire voyage as one scrollable log
 * - FloatingActionBar provides quick access to Position, Weather, Note, Operation
 * - OperationDataModal collects operation-specific data before starting
 * - All checks (pre-departure, operation) appear inline and are tickable
 *
 * FT-284 (replaces FT-270 two-modal pattern):
 *   FAB tap → VoyageDocument opens data-capture modal (Position / Weather /
 *   Note / Fuel / Operation Start) which now hosts an InlineTimeField. The
 *   modal calls `onSubmit(payload, timeOptions)` (or `onConfirm` for fuel).
 *   VoyageDocument forwards to UnderwayPhase's `handleLog*` handlers. Each
 *   handler:
 *     1. Runs `validateAgainstVoyageBounds(timeOptions, voyage, currentVessel)`
 *        from `../../../../utils/voyageBounds.js`.
 *     2. On boundary error → returns `{ boundaryError }` so the modal can
 *        surface it via <ModalErrorBanner> and stay open.
 *     3. On success → assembles the V2 options object (recordedAtDevice +
 *        networkStatusAtRecord audit context) and invokes the V2 hook.
 *
 * FT-285 Item 2: the All Fast TimePickerModal step is now collapsed into the
 * ConfirmSignOffModal (which hosts an InlineTimeField via the optional
 * timeField prop). Let Go has its own inline field on LetGoReviewScreen
 * (FT-285 Item 1) and Complete Voyage has one on ArrivalPhase
 * (FT-285 Item 3). The standalone TimePickerModal is now used only by the
 * legacy PreDeparturePhase / PreArrivalPhase mounts (out of FT-285 scope).
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';

// Unified Voyage Log - V2
import VoyageDocument from './VoyageDocument';
import { useVessel } from '../../../../../contexts/VesselContext';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { VOYAGE_STATUS } from '../../../../../config/operationsConfig';
// FT-284: shared bounds validator (pure, unit-testable).
import { validateAgainstVoyageBounds } from '../../../../../utils/voyageBounds';
// FT-285 Item 2: All Fast InlineTimeField hook (lives inside ConfirmSignOffModal).
import { useInlineEventTime } from './useInlineEventTime';
// Sub-ticket C: voyage-plan underway card. Mounted ABOVE VoyageDocument so the
// Master sees plan items at the top of the viewport (collapsed-by-default on
// phone per B9). Card-internal guards handle non-attached / pre-B voyages.
import VoyagePlanUnderwayCard from './VoyagePlanUnderwayCard';

// Pass 2o: ad-hoc activity window. Triplicated predicate (anti-Pass-2b — do
// NOT extract). Mirrored in VoyageDocument + VoyagePlanUnderwayCard.
const ACTIVE_OPERATION_ADHOC_WINDOW_MS = 60 * 60 * 1000;  // 60 minutes
// eslint-disable-next-line complexity
function findActiveSection(log, planItems, nowMs) {
  const entries = log || [];
  const sections = entries.filter(e => e && e.type === 'section' && e.operationId);
  const completedIds = new Set(
    entries.filter(e => e && e.type === 'operation_complete' && e.operationId).map(e => e.operationId)
  );
  const items = planItems || {};
  for (const section of [...sections].reverse()) {
    if (completedIds.has(section.operationId)) continue;
    if (section.planItemId) {
      const itemStatus = items[section.planItemId]?.status;
      if (itemStatus && itemStatus !== 'completed' && itemStatus !== 'skipped') return section;
      if (itemStatus === 'completed' || itemStatus === 'skipped') continue;
      // itemStatus undefined: fall through to ad-hoc window
    }
    const lastActivityMs = entries.reduce((max, e) => {
      if (!e || e.type !== 'check' || e.operationId !== section.operationId) return max;
      const t = e.completedAt || e.skippedAt || null;
      if (!t) return max;
      const ms = new Date(t).getTime();
      return Number.isFinite(ms) && ms > max ? ms : max;
    }, 0);
    if (lastActivityMs > 0 && nowMs - lastActivityMs <= ACTIVE_OPERATION_ADHOC_WINDOW_MS) return section;
  }
  return null;
}

// Pass 2t — single nested-rAF scroll helper. Mirrors Pass 2H pattern.
// Module-scope so all 3 call sites share one implementation (handlePrepareForArrival
// success, handlePrepareForArrival refuse, handleStartOperation success).
// Anti-Pass-2b note: in-file extraction only, NOT across files. Selector fallback
// handles fresh-section case (no rendered CompleteOperationRow yet → fall back to
// plan-row testid).
function scrollToOperationRow(operationId, fallbackSelector) {
  if (typeof window === 'undefined') return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (typeof document === 'undefined') return;
      let el = null;
      if (operationId) {
        el = document.querySelector(`[data-operation-id="${operationId}"]`);
      }
      if (!el && fallbackSelector) {
        el = document.querySelector(fallbackSelector);
      }
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });
}

/**
 * UnderwayPhase Component
 *
 * Simplified V2 version - delegates all rendering to VoyageDocument.
 * Operations flow: FloatingActionBar → OperationDataModal → startOperationV2 → unifiedLog
 */
const UnderwayPhase = ({ context, voyage, setShowEditVoyage, onClose }) => {
  // Pass 2f §1.1 — pull `logbook` off the context alongside the voyage hook so
  // handleAllFastConfirm (§1.2) can mirror the All Fast voyage event into the
  // main Logbook. Mirrors PreArrivalPhase.js:206-219's existing pattern.
  const { voyage: voyageHook, logbook } = context;
  const {
    // V2 Functions - operations appear inline in unifiedLog
    startOperationV2,
    completeCheckV2,
    skipCheckV2,
    completeOperationV2,        // FT-283: prefilled-options aware no-skip path
    bulkSkipAndComplete,        // FT-283: atomic skip + complete (race-free)
    logPositionV2,
    logWeatherV2,
    logNoteV2,
    logFuelReading,  // Fuel level logging
    allFast,  // FT-202: ALL FAST milestone
    // Sub-ticket C: per-plan-item state mutations (composed in useVoyages
    // from useVoyagePlanItemState — same access pattern as completeCheckV2 etc.).
    updatePlanItemState,
    skipPlanItemState,
    // FT-489: Orphan recovery — discard a stale pre_arrival section from the log.
    discardOrphanedOperation,
  } = voyageHook;

  // FT-202: ALL FAST modal state
  const [showAllFastModal, setShowAllFastModal] = useState(false);
  // FT-489: Orphan pre_arrival section recovery prompt state.
  // Non-null when an orphaned section is detected; holds the section log entry
  // so handlers have the operationId for Resume/Discard actions.
  const [orphanPreArrSection, setOrphanPreArrSection] = useState(null);
  // FT-285 Item 2: standalone TimePickerModal removed — the sign-off modal
  // now hosts an InlineTimeField. showTimePicker / pendingTimeOptions deleted.
  const { currentVessel } = useVessel();
  // Sub-ticket C: currentUser for resolvedBy attribution after a successful
  // waypoint position log → updatePlanItemState completion patch.
  const { currentUser } = usePermissions();

  // ────────────────────────────────────────────────────────────────────────
  // Sub-ticket C: plan-item interaction context.
  //
  // Two separate "context" pieces flow down to VoyageDocument:
  //
  //   planItemOperationContext = { planItemId, operationType } | null
  //     → tap on an operation-ref row in the plan card. VoyageDocument force-
  //       opens OperationDataModal directly (bypassing OperationPickerModal).
  //       handleCompleteOperation reads `planItemContextRef.current?.planItemId`
  //       and threads it through completeOperationV2 / bulkSkipAndComplete.
  //
  //   planItemPositionContext = { planItemId, label } | null
  //     → tap on a waypoint row in the plan card. VoyageDocument force-opens
  //       PositionInputModal. PositionInputModal contract is INTENTIONALLY
  //       UNCHANGED (FT-284 stable; brief §6.3 LOCKED) — the plumbing lives
  //       at this level. handleLogPosition reads the ref and threads planItemId
  //       through logPositionV2, then on success writes the per-item completion.
  //
  // Refs mirror state for closure-stable async reads inside handlers that
  // capture stale `state` if relied on directly.
  // ────────────────────────────────────────────────────────────────────────
  const [planItemOperationContext, setPlanItemOperationContext] = useState(null);
  const [planItemPositionContext, setPlanItemPositionContext] = useState(null);
  const planItemOperationContextRef = useRef(null);
  const planItemPositionContextRef = useRef(null);
  useEffect(() => { planItemOperationContextRef.current = planItemOperationContext; }, [planItemOperationContext]);
  useEffect(() => { planItemPositionContextRef.current = planItemPositionContext; }, [planItemPositionContext]);

  // Pass 2t — burst-tap debounce (<~32ms re-entry into start handlers). Reset
  // via nested-rAF so React has committed before the next allowed entry.
  const startInFlightRef = useRef(false);
  // Pass 2t — aria-live toast for refused duplicate taps. Auto-clears after 3s.
  const refusedToastTimerRef = useRef(null);
  const [refusedToastMessage, setRefusedToastMessage] = useState(null);
  const fireRefusedToast = useCallback((label) => {
    setRefusedToastMessage(label);
    if (refusedToastTimerRef.current) clearTimeout(refusedToastTimerRef.current);
    refusedToastTimerRef.current = setTimeout(() => setRefusedToastMessage(null), 3000);
  }, []);
  useEffect(() => () => {
    if (refusedToastTimerRef.current) clearTimeout(refusedToastTimerRef.current);
  }, []);

  // [Pass 1 FIX R1-C1 defensive cleanup] Clear stashed plan-item context if an
  // active operation of a DIFFERENT operationType exists — guards against the
  // "start plan A (type X), then FAB-start B (type Y) without completing A"
  // cross-contamination class. activeOperation derived inline from unifiedLog
  // (UnderwayPhase has no activeOperation prop; mirrors VoyageDocument:290-298).
  useEffect(() => {
    const ctx = planItemOperationContext;
    if (!ctx) return;
    // [Pass 2o] Shared predicate — same active notion as VoyageDocument.
    const log = voyage?.unifiedLog || [];
    const planItems = voyage?.voyagePlanItems || {};
    const ao = findActiveSection(log, planItems, Date.now());
    if (ao && ao.operationType !== ctx.operationType) {
      setPlanItemOperationContext(null);
    }
  }, [voyage?.unifiedLog, voyage?.voyagePlanItems, planItemOperationContext]);

  const clearPlanItemOperationContext = useCallback(() => {
    setPlanItemOperationContext(null);
  }, []);
  const clearPlanItemPositionContext = useCallback(() => {
    setPlanItemPositionContext(null);
  }, []);

  // Sub-ticket C: card-tap handlers wired into VoyagePlanUnderwayCard.
  //
  //   handleStartOperationForPlanItem:
  //     Card row tap on an operation-ref item. Stash the planItemId +
  //     operationType + snapshot in state — VoyageDocument observes the
  //     context via the new effect (see VoyageDocument.js
  //     'Sub-ticket C operation auto-open' effect) and force-opens
  //     OperationDataModal directly, BYPASSING OperationPickerModal so the
  //     Master goes straight into data capture for the planned operation.
  //
  //   handleLogPositionForWaypoint:
  //     Card row tap on a waypoint item. Stash the planItemId + label in
  //     state — VoyageDocument observes and force-opens PositionInputModal.
  //     PositionInputModal's contract is intentionally UNCHANGED per
  //     brief §6.3 LOCKED — the planItemId plumbing lives at this level via
  //     planItemPositionContextRef, picked up by handleLogPosition above.
  const handleStartOperationForPlanItem = useCallback((item) => {
    if (!item || item.kind !== 'operation_ref' || !item.operationId) return;
    if (startInFlightRef.current) return;

    // Pass 2t §G4 — defence-in-depth dedup. handleTap inside VoyagePlanUnderwayCard
    // also checks, but this is a separate code path (e.g. future callers) that
    // reads a fresh voyage snapshot. Belt-and-braces per HARD RULE
    // feedback_multi_agent_belt_braces.
    const log = voyage?.unifiedLog || [];
    const completedIds = new Set(
      log.filter(e => e?.type === 'operation_complete' && e.operationId).map(e => e.operationId)
    );
    const existing = log.find(e =>
      e?.type === 'section' && e.planItemId === item.id && !completedIds.has(e.operationId)
    );
    if (existing) {
      scrollToOperationRow(existing.operationId, `[data-testid="plan-item-row-${item.id}"]`);
      fireRefusedToast(`${item.operationSnapshot?.name || 'Operation'} already started`);
      return;
    }

    setPlanItemOperationContext({
      planItemId: item.id,
      operationType: item.operationId,
      operationSnapshot: item.operationSnapshot || null
    });
  }, [voyage?.unifiedLog, fireRefusedToast]);

  const handleLogPositionForWaypoint = useCallback((item) => {
    if (!item || item.kind !== 'waypoint') return;
    setPlanItemPositionContext({
      planItemId: item.id,
      label: item.label || 'Waypoint'
    });
  }, []);

  // FT-270 §17.7: derived flag for "can the FAB show in-voyage event buttons?"
  // The hook-level state-machine guard is the safety net; this is the primary
  // affordance (hide the buttons entirely when status is not UNDERWAY/ARRIVAL).
  const inVoyageEventsAllowed = voyage?.status === VOYAGE_STATUS.UNDERWAY
    || voyage?.status === VOYAGE_STATUS.ARRIVAL;

  // FT-284: resolved timezone for the inline time field, threaded through
  // VoyageDocument to every in-voyage modal. Voyage-first per resolveBoundary
  // chain in voyageBounds.js.
  const resolvedTimezone = voyage?.preferredTimezone
    || currentVessel?.settings?.timezone
    || currentVessel?.timezone
    || 'UTC';

  // FT-285 Item 2: dedicated hook instance for the All Fast sign-off modal's
  // inline event-time field. Lives in UnderwayPhase so the hook is stable
  // across re-renders; threaded down through VoyageDocument to
  // ConfirmSignOffModal. Declared AFTER resolvedTimezone (TDZ-safe).
  const allFastTimeField = useInlineEventTime(resolvedTimezone);

  // ============================================================================
  // V2 VOYAGE DOCUMENT HANDLERS
  // ============================================================================

  // V2: Complete a check (works for pre-departure AND operation checks)
  const handleCompleteCheck = useCallback((entry) => {
    if (!voyage?.id || !entry?.id) return;
    completeCheckV2(voyage.id, entry.id);
  }, [voyage?.id, completeCheckV2]);

  // V2: Skip a check
  const handleSkipCheck = useCallback((entry, reason, notes) => {
    if (!voyage?.id || !entry?.id) return;
    skipCheckV2(voyage.id, entry.id, reason, notes);
  }, [voyage?.id, skipCheckV2]);

  // ============================================================================
  // FT-284: per-event handlers — accept (payload, timeOptions), validate
  // against voyage bounds, assemble options, invoke V2 hook.
  // ============================================================================

  // V2 + FT-284: Log position
  // Sub-ticket C: when a plan-card waypoint tap is in flight (waypointPrefill
  // / planItemPositionContext set), merge planItemId into the V2 options so
  // the resulting `position` unifiedLog entry carries it top-level, AND mark
  // the plan item completed (with linkedLogEntryId) after a successful write.
  const handleLogPosition = useCallback(async (positionData, timeOptions) => {
    if (!voyage?.id) return;
    if (!positionData?.lat || !positionData?.lon) return;

    const boundary = validateAgainstVoyageBounds(timeOptions, voyage, currentVessel);
    if (boundary.error) return { boundaryError: boundary.error };

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    // Sub-ticket C: read planItemId from the ref (closure-stable async read).
    const planCtx = planItemPositionContextRef.current;
    const options = {
      ...(timeOptions || {}),
      recordedAtDevice: new Date().toISOString(),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
      ...(planCtx?.planItemId ? { planItemId: planCtx.planItemId } : {})
    };

    const entry = await logPositionV2(
      voyage.id,
      positionData.lat,
      positionData.lon,
      positionData.accuracy ?? null,
      positionData.source ?? null,
      positionData.manualReason ?? null,
      options
    );

    // Sub-ticket C: post-success, flip the plan item to completed with FK to
    // the new log entry. Only fires when we know the planItemId AND logPositionV2
    // produced an entry (returns null on guard failure).
    if (planCtx?.planItemId && entry && updatePlanItemState) {
      await updatePlanItemState(voyage.id, planCtx.planItemId, {
        status: 'completed',
        linkedLogEntryId: entry.id,
        resolvedAt: entry.timestamp || entry.eventTime || new Date().toISOString(),
        resolvedBy: currentUser?.id || null,
        resolvedByName: currentUser?.name || 'Unknown'
      });
      // Clear the prefill context so future FAB-tap position logs are not
      // accidentally tagged with this planItemId.
      setPlanItemPositionContext(null);
    }
  }, [voyage, currentVessel, logPositionV2, updatePlanItemState, currentUser]);

  // V2 + FT-284: Log weather
  const handleLogWeather = useCallback(async (weatherData, timeOptions) => {
    if (!voyage?.id || !weatherData) return;

    const boundary = validateAgainstVoyageBounds(timeOptions, voyage, currentVessel);
    if (boundary.error) return { boundaryError: boundary.error };

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const options = {
      ...(timeOptions || {}),
      recordedAtDevice: new Date().toISOString(),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {})
    };

    await logWeatherV2(voyage.id, weatherData, options);
  }, [voyage, currentVessel, logWeatherV2]);

  // V2 + FT-284: Log note (supports standalone notes, annotations, AND the
  // silent compass-check 1-arg call path from VoyageDocument.js:601).
  //
  // §10.4 contract:
  //   - 3-arg call from FAB note modal: (noteText, linkedEntryId, timeOptions)
  //   - 1-arg call from compass-check:   (noteText)
  //     → linkedEntryId, timeOptions both `undefined` → V2 hook auto-stamps Now.
  const handleLogNote = useCallback(async (noteText, linkedEntryId, timeOptions) => {
    if (!voyage?.id || !noteText) return;
    const normalizedLinkedId = linkedEntryId ?? null;
    const normalizedTimeOptions = timeOptions ?? undefined;

    // undefined timeOptions → V2 hook auto-stamps Now silently (compass path).
    if (normalizedTimeOptions) {
      const boundary = validateAgainstVoyageBounds(normalizedTimeOptions, voyage, currentVessel);
      if (boundary.error) return { boundaryError: boundary.error };
    }

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const options = {
      ...(normalizedTimeOptions || {}),
      recordedAtDevice: new Date().toISOString(),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {})
    };

    await logNoteV2(voyage.id, noteText, normalizedLinkedId, options);
  }, [voyage, currentVessel, logNoteV2]);

  // V2 + FT-284: Log fuel reading (mid-voyage). Preserves the existing
  // percentage/value precedence — the V2 hook expects a scalar fuelLevel.
  const handleLogFuel = useCallback(async (fuelData, timeOptions) => {
    if (!voyage?.id || !fuelData) return;

    const boundary = validateAgainstVoyageBounds(timeOptions, voyage, currentVessel);
    if (boundary.error) return { boundaryError: boundary.error };

    const fuelLevel = fuelData?.percentage ?? fuelData?.value;

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const options = {
      ...(timeOptions || {}),
      recordedAtDevice: new Date().toISOString(),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {})
    };

    await logFuelReading(voyage.id, fuelLevel, '', options);
  }, [voyage, currentVessel, logFuelReading]);

  // V2 + FT-284: Start operation — called from VoyageDocument after
  // OperationDataModal submits.
  // Pass 2t — burst-tap debounce + scroll-on-success. The dedup invariant
  // here is weaker than the plan-item path (FAB ad-hoc operations are
  // legitimately re-startable per the predicate's design), so we lean on
  // (a) startInFlightRef for sub-frame burst protection and (b) startOperationV2's
  // in-updater dedup as the last line of defence.
  const handleStartOperation = useCallback(async (operationType, operationData, timeOptions) => {
    if (!voyage?.id || !operationType) return;
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;

    try {
      const boundary = validateAgainstVoyageBounds(timeOptions, voyage, currentVessel);
      if (boundary.error) return { boundaryError: boundary.error };

      const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
      // Pass 2o: timeOptions may carry planItemId (spread preserves it).
      const options = {
        ...(timeOptions || {}),
        recordedAtDevice: new Date().toISOString(),
        ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {})
      };

      const startedOperationId = await startOperationV2(voyage.id, operationType, operationData || {}, options);
      if (startedOperationId) {
        scrollToOperationRow(startedOperationId, null);
      }
      return startedOperationId;
    } finally {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { startInFlightRef.current = false; });
      });
    }
  }, [voyage, currentVessel, startOperationV2]);

  // ============================================================================
  // FT-283 §4: COMPLETE OPERATION HANDLERS
  // ============================================================================

  // FT-283 §4: handleCompleteOperation — prefilled-options no-skip path.
  // Strip prefilled/clockSkew/extraAuditData at this boundary; clockSkew rides
  // in extraAuditData (NOT auditContext — fireInVoyageAudit only extracts
  // networkStatusAtRecord from auditContext).
  const handleCompleteOperation = useCallback(async (operationId, prefilledOptions = null) => {
    if (!voyage?.id || !operationId) return;
    if (!prefilledOptions) {
      console.warn('[UnderwayPhase] handleCompleteOperation called without prefilledOptions; ignoring.');
      return;
    }
    // eslint-disable-next-line no-unused-vars
    const { prefilled, clockSkew, extraAuditData, eventTime, eventTimezone, planItemId: prefilledPlanItemId, ...rest } = prefilledOptions;

    const boundary = validateAgainstVoyageBounds(
      { eventTime, delayReason: rest.delayReason },
      voyage,
      currentVessel
    );
    if (boundary.error) return { boundaryError: boundary.error };

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false)
      ? 'offline'
      : null;

    // Sub-ticket C: prefilledPlanItemId comes from VoyageDocument
    // (handleCompleteOperationRequest reads planItemOperationContextRef and
    // spreads it into prefilledOptions). Fall back to this hook's own ref
    // for belt-and-braces in case VoyageDocument missed a hand-off.
    const planItemId = prefilledPlanItemId
      || planItemOperationContextRef.current?.planItemId
      || null;

    const options = {
      ...rest,
      eventTime,
      eventTimezone: eventTimezone || resolvedTimezone,
      recordedAtDevice: new Date().toISOString(),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
      ...(planItemId ? { planItemId } : {}),
      extraAuditData: {
        ...(extraAuditData || {}),
        ...(clockSkew ? { clockSkew: true } : {})
      }
    };

    await completeOperationV2(voyage.id, operationId, options);

    // Sub-ticket C: after a successful complete-operation tied to a plan item,
    // clear the operation context so future operation completions don't get
    // wrongly tagged. The observer effect in VoyagePlanUnderwayCard also
    // flips the per-item state to 'completed' as belt-and-braces.
    if (planItemId) {
      setPlanItemOperationContext(null);
    }
  }, [voyage, currentVessel, completeOperationV2, resolvedTimezone]);

  // FT-283 §4 + §6.2: handleBulkSkipAndComplete wrapper. VoyageDocument
  // pre-stamps eventTimezone / recordedAtDevice / auditContext / extras
  // (see VoyageDocument.handleBulkSkipConfirm lines 740-770). We only run
  // the bounds check and forward.
  const handleBulkSkipAndComplete = useCallback(async (voyageId, skipItems, completeOptions) => {
    if (!voyage?.id) return;
    const boundary = validateAgainstVoyageBounds(
      { eventTime: completeOptions?.eventTime, delayReason: completeOptions?.delayReason },
      voyage,
      currentVessel
    );
    if (boundary.error) return { boundaryError: boundary.error };
    // Sub-ticket C: belt-and-braces — merge planItemId from the ref if
    // VoyageDocument did not already spread it into completeOptions.
    const planItemId = completeOptions?.planItemId
      || planItemOperationContextRef.current?.planItemId
      || null;
    const finalOptions = planItemId && !completeOptions?.planItemId
      ? { ...completeOptions, planItemId }
      : completeOptions;
    await bulkSkipAndComplete(voyageId, skipItems, finalOptions);

    if (planItemId) {
      setPlanItemOperationContext(null);
    }
  }, [voyage, currentVessel, bulkSkipAndComplete]);

  // ============================================================================
  // HANDLER FOR PRE-ARRIVAL (FT-202)
  // ============================================================================

  // FT-202: Start pre_arrival as an inline operation instead of screen takeover.
  // FT-284 note: pre_arrival is triggered by an explicit "Prepare for Arrival"
  // CTA, not the FAB Operation Start path. It has no data-capture modal and
  // no inline time field — fires Now immediately.
  // FT-283 §8.10: pre-arrival START audit parity — stamp eventTime,
  // eventTimezone, recordedAtDevice + networkStatusAtRecord like every other
  // V2 handler so audit rows are consistent.
  // Pass 2h §1.2 — convert to async so we can await startOperationV2 and
  // schedule a scroll-into-view affordance on the just-mounted
  // CompleteOperationRow. The Pre-Arr phase marker (VoyagePlanUnderwayCard
  // onTapPreArrival) and the FAB Arrive button (FloatingActionBar arrive
  // action) both dispatch this handler; both currently ignore the return
  // value, so the async conversion is externally transparent. Mirror of the
  // Pass 2e All Fast scroll pattern at ArrivalPhase.js:161.
  const handlePrepareForArrival = useCallback(async () => {
    if (!voyage?.id) return;
    // Pass 2t — burst-tap debounce. Single re-entry guard for the prepare-for-arrival
    // path. Reset via nested-rAF in `finally` so React has committed before the
    // next allowed entry. Catches sub-frame rapid taps before they reach
    // startOperationV2's dedup layer (extra defence).
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;

    try {
      const log = voyage?.unifiedLog || [];
      const completedIds = new Set(
        log.filter(e => e?.type === 'operation_complete' && e.operationId).map(e => e.operationId)
      );

      // Pass 2t §G3 — STRENGTHENED guard. Block ANY uncompleted pre_arrival
      // section, not just when operation_complete exists. Closes the in-flight
      // window that allowed bug-#039 to write 3 Pre-Arrival sections in 2.7s.
      // FT-489: Instead of silent scroll+toast, surface Resume/Discard prompt
      // so the user has a recovery path (e.g. after Android OS back-swipe).
      const uncompletedPreArr = log.find(e =>
        e?.type === 'section' && e.operationType === 'pre_arrival' && e.operationId && !completedIds.has(e.operationId)
      );
      if (uncompletedPreArr) {
        console.warn('[UnderwayPhase] pre-arrival section already in flight; showing Resume/Discard prompt.');
        setOrphanPreArrSection(uncompletedPreArr);
        return;
      }

      // FT-285 Item 4: defence-in-depth hard-lock. The FAB / LogPickerModal
      // already disable the Arrive button via preArrivalDone, but a stale render
      // could still fire this handler. Bail if pre_arrival operation_complete
      // already exists for this voyage — prevents a duplicate
      // operation_complete audit row (MCA-relevant state transition).
      // PRESERVED for FT-285 belt-and-braces; the Pass 2t guard above subsumes
      // most cases but this remains as a fall-through safety net.
      const preArrivalSection = log.find(
        e => e.type === 'section' && e.operationType === 'pre_arrival' && e.operationId
      );
      if (preArrivalSection) {
        const alreadyComplete = log.some(
          e => e.type === 'operation_complete' && e.operationId === preArrivalSection.operationId
        );
        if (alreadyComplete) {
          console.warn('[UnderwayPhase] Pre-arrival already complete; ignoring duplicate prepare-for-arrival.');
          return;
        }
      }

      const now = new Date().toISOString();
      const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false)
        ? 'offline'
        : null;
      // Pass 2h §1.2 — capture the operationId so we can scroll to the
      // matching [data-operation-id] on CompleteOperationRow once it mounts.
      const operationId = await startOperationV2(voyage.id, 'pre_arrival', {}, {
        eventTime: now,
        eventTimezone: resolvedTimezone,
        recordedAtDevice: now,
        ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {})
      });

      // Pass 2h §1.2 — defensive guards before scheduling the scroll:
      // startOperationV2 returns null on RBAC fail / voyage-not-found.
      // Pass 2t — extracted to module-scope scrollToOperationRow helper. The
      // fallback selector handles fresh-section case where CompleteOperationRow
      // hasn't rendered yet within the 32ms rAF window.
      if (!operationId) return;
      scrollToOperationRow(operationId, '[data-testid="plan-phase-row-pre-arrival"]');
    } finally {
      // Pass 2t — release after React has committed (nested rAF) so the next
      // tap re-evaluates against the new log state.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { startInFlightRef.current = false; });
      });
    }
  }, [voyage, currentVessel, startOperationV2, resolvedTimezone, setOrphanPreArrSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // FT-489: Orphan pre_arrival recovery handlers.
  // handleOrphanResume — scrolls to the existing in-log section so the user
  // can continue the checklist where they left off.
  // handleOrphanDiscard — removes all entries for the orphaned operationId
  // then starts a fresh pre_arrival by calling startOperationV2 directly
  // (skipEarlyDedup:true bypasses the stale-closure early check; the
  // authoritative in-updater re-check in startOperationV2 still runs).
  const handleOrphanResume = useCallback(() => {
    if (!orphanPreArrSection) return;
    scrollToOperationRow(
      orphanPreArrSection.operationId,
      '[data-testid="plan-phase-row-pre-arrival"]'
    );
    setOrphanPreArrSection(null);
  }, [orphanPreArrSection]);

  const handleOrphanDiscard = useCallback(async () => {
    if (!orphanPreArrSection || !voyage?.id) return;
    await discardOrphanedOperation(voyage.id, orphanPreArrSection.operationId);
    setOrphanPreArrSection(null);
    // Call startOperationV2 directly rather than handlePrepareForArrival, which
    // reads `voyage` from the stale prop (React hasn't re-rendered yet after the
    // discard). startOperationV2 reads from the hook's internal state — already
    // updated by the functional updater — so the orphan is gone before it checks.
    const now = new Date().toISOString();
    const networkStatusAtRecord =
      (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    await startOperationV2(voyage.id, 'pre_arrival', {}, {
      eventTime: now,
      eventTimezone: resolvedTimezone,
      recordedAtDevice: now,
      skipEarlyDedup: true,
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
    });
  }, [orphanPreArrSection, voyage, discardOrphanedOperation, startOperationV2, resolvedTimezone]);

  // FT-285 Item 2: ALL FAST flow collapsed from TimePickerModal →
  // ConfirmSignOffModal to a single sign-off modal that hosts an
  // InlineTimeField. handleAllFast opens the sign-off modal directly;
  // handleAllFastConfirm reads timeOptions off the sign-off payload (the
  // modal attaches a fresh snapshot at submit). All Fast IS the arrival
  // boundary — no voyage-bounds check needed; allFast V2 hook runs its own
  // state-machine validation.
  const handleAllFast = useCallback(() => {
    setShowAllFastModal(true);
  }, []);

  // Pass 2f §1.2 [v2 FIX SF1] — mirror PreArrivalPhase.js:113-121 verbatim so
  // the Logbook write below can populate `duration` correctly. The voyage
  // object has NO `duration` field; this memo derives it from `departureTime`.
  const voyageDuration = useMemo(() => {
    if (!voyage.departureTime) return null;
    const start = new Date(voyage.departureTime);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }, [voyage.departureTime]);

  const handleAllFastConfirm = useCallback(async (signOffData) => {
    if (!voyage?.id) return;
    // signOffData = { confirmedBy, role, confirmedAt, timeOptions }
    // Pass 2f §1.2 — capture the V2 hook result so we can gate the main-Logbook
    // write on success. allFast() runs state-machine validation internally and
    // returns { success: false } when blocked (e.g. active operations still
    // open). We must NOT log to the Logbook in the blocked case.
    const result = await allFast(voyage.id, signOffData?.timeOptions || undefined);

    if (result?.success) {
      // Mirror the All Fast voyage event into the main Logbook so the
      // regulatory record carries the paired arrival event matching Let Go.
      // Pattern is verbatim from PreArrivalPhase.js:206-219 (the legacy
      // pre-FT-285 sign-off flow), variable-swapped for UnderwayPhase scope.
      await logbook.logVoyageEvent({
        eventType: 'all_fast',
        voyageId: voyage.id,
        voyageNumber: voyage.voyageNumber || 'Unknown',
        location: voyage.to?.name || voyage.arrivalLocation || 'Unknown',
        duration: voyageDuration || 'Unknown',
        operations: voyage.operations?.length || 0,
        timestamp: signOffData?.timeOptions?.eventTime || new Date().toISOString()
      });
    }

    setShowAllFastModal(false);
  }, [voyage, allFast, logbook, voyageDuration]);

  const handleAllFastCancel = useCallback(() => {
    setShowAllFastModal(false);
  }, []);

  // ============================================================================
  // RENDER - Just VoyageDocument, nothing else
  // ============================================================================

  // FT-270 §17.7: hide the in-voyage FAB triggers when status is not
  // UNDERWAY or ARRIVAL. We pass no-op handlers so VoyageDocument can still
  // render but the FAB buttons cannot fire the hooks. The hook-level guard
  // is the safety net behind this.
  const noop = useCallback(() => {}, []);
  const guardedLogPosition = inVoyageEventsAllowed ? handleLogPosition : noop;
  const guardedLogWeather = inVoyageEventsAllowed ? handleLogWeather : noop;
  // Note handler is NEVER guarded — the compass-check silent 1-arg path
  // must work in any voyage status (it's an auto-log from a check completion).
  const guardedLogNote = handleLogNote;
  const guardedLogFuel = inVoyageEventsAllowed ? handleLogFuel : noop;
  const guardedStartOperation = inVoyageEventsAllowed ? handleStartOperation : noop;

  // Sub-ticket C: render the plan card as the topSlot of VoyageDocument. The
  // card has its own internal guards for `voyagePlanResolution` (pre-B voyages
  // → null; non-attached → banner only; attached + items → full card). The
  // outer ternary here is the belt-and-braces backward-compat guard called for
  // in CODING-BRIEF-C §7.1.
  const planCardSlot = voyage?.voyagePlanResolution ? (
    <VoyagePlanUnderwayCard
      voyage={voyage}
      onUpdatePlanItem={updatePlanItemState}
      onSkipPlanItem={skipPlanItemState}
      onLogPositionForWaypoint={handleLogPositionForWaypoint}
      onStartOperationForPlanItem={handleStartOperationForPlanItem}
      vesselTimezone={resolvedTimezone}
      onTapPreArrival={handlePrepareForArrival}                  // Pass 2e — same handler the FAB Arrive button fires.
      onRefusedDuplicateTap={(payload) => {
        // Pass 2t — scroll is handled inside VoyagePlanUnderwayCard.handleTap
        // (has item.id in scope). UnderwayPhase only owns the toast text.
        fireRefusedToast(`${payload?.label || 'Operation'} already started`);
      }}
    />
  ) : null;

  return (
    <>
    <VoyageDocument
      voyage={voyage}
      onEditVoyage={() => setShowEditVoyage?.(true)}
      onCompleteCheck={handleCompleteCheck}
      onSkipCheck={handleSkipCheck}
      onLogPosition={guardedLogPosition}
      onLogWeather={guardedLogWeather}
      onLogNote={guardedLogNote}
      onLogFuel={guardedLogFuel}
      onStartOperation={guardedStartOperation}
      onCompleteOperation={handleCompleteOperation}          // FT-283
      onBulkSkipAndComplete={handleBulkSkipAndComplete}      // FT-283
      onPrepareForArrival={handlePrepareForArrival}
      onClose={onClose}
      showAllFastModal={showAllFastModal}
      onAllFast={handleAllFast}
      onAllFastConfirm={handleAllFastConfirm}
      onAllFastCancel={handleAllFastCancel}
      // FT-285 Item 2: inline event-time field hook for the All Fast
      // sign-off modal. Threaded through VoyageDocument to ConfirmSignOffModal.
      allFastTimeField={allFastTimeField}
      vesselTimezone={resolvedTimezone}
      // Sub-ticket C: plan-card slot + per-tap modal-routing contexts.
      // VoyageDocument observes the contexts via useEffect and force-opens
      // OperationDataModal (operation-ref tap) / PositionInputModal (waypoint
      // tap) directly — bypassing the FAB picker path so the Master goes
      // straight into the data-capture flow tied to the tapped plan item.
      topSlot={planCardSlot}
      planItemOperationContext={planItemOperationContext}
      planItemPositionContext={planItemPositionContext}
      onClearPlanItemOperationContext={clearPlanItemOperationContext}
      onClearPlanItemPositionContext={clearPlanItemPositionContext}
    />
    {refusedToastMessage && (
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-lg"
        data-testid="duplicate-tap-refused-toast"
      >
        {refusedToastMessage}
      </div>
    )}
    {/* FT-489: Orphan pre_arrival recovery prompt. Shown when the user taps Arrive
        and an uncompleted pre-arrival section is found in the log (e.g. after an
        Android back-swipe exits the voyage screen mid-checklist). Offers Resume
        (scroll to existing checklist) or Discard (remove orphan + start fresh). */}
    {orphanPreArrSection && (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
        data-testid="orphan-pre-arrival-prompt"
      >
        <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Pre-arrival in progress</h3>
          <p className="text-sm text-gray-600 mb-6">
            A pre-arrival checklist was started but not completed. Would you like to continue where you left off, or discard it and start again?
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleOrphanResume}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium"
              data-testid="orphan-pre-arrival-resume"
            >
              Resume
            </button>
            <button
              onClick={handleOrphanDiscard}
              className="w-full py-3 px-4 text-red-600 rounded-lg text-sm font-medium border border-red-200"
              data-testid="orphan-pre-arrival-discard"
            >
              Discard and start again
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default UnderwayPhase;
